import { S } from '../core/game';
import { dayId, XiaoyuPhase } from '../core/state';
import { npcName, objName } from '../i18n/labels';
import { m } from '../i18n';
import { NPCS } from '../world/npcs';
import { mapSize, type MapId } from '../world/map';
import { T } from '../world/tiles';
import { targetRect, type World } from '../world/world';
import { Ground } from './ground';
import { buildFoliage, Clouds, drawFoliage, setDetail, wind, type Foliage } from './nature';
import { drawLighting, skyTint } from './lighting';
import { ellipse, pill, rect, setSun, shadow, type Ctx } from './paint';
import { buildProps, setPropScale, type PropSprite } from './props';
import { drawCat, drawCharacter, PLAYER_LOOKS } from './sprites';
import { drawHorse, drawPet } from './pets';
import { OUTFITS, riding } from '../content/places';
import { drawBus } from '../world/ambient';
import { drawBike } from './props4';
import { drawEbike, drawKick } from './vehicles';
import { drawWeather, shakeOffset } from './weather';
import { biomeOf, drawBackdrop, drawBuildingShadows, Flocks, nightOf } from './atmosphere';
import { npcMarker } from '../content/story';

export const PET_EMOJI = {
  rabbit: '🐰',
  cat: '🐱',
  dog: '🐶',
  hedgehog: '🦔',
  hamster: '🐹',
  croc: '🐊',
  fish: '🐟',
  dino: '🦖',
} as const;

export interface Marker {
  kind: 'clover' | 'checkpoint' | 'rustle' | 'glint';
  x: number;
  y: number;
  label?: string;
}

export interface Overlay {
  /** Where the guide arrow should point (world coordinates on the current map). */
  guide: { x: number; y: number } | null;
  markers: Marker[];
  /** Ask whether an object prop is currently visible. */
  propVisible?: (id: string) => boolean;
}

interface Drawable {
  y: number;
  draw(): void;
}

/** Composes a frame: tile layer, depth-sorted props/characters, effects, lighting, hints. */
export class Renderer {
  private ctx: Ctx;
  private grounds = new Map<MapId, Ground>();
  private foliage = new Map<MapId, Foliage>();
  private clouds = new Map<MapId, Clouds>();
  private lastT = 0;
  private props = new Map<MapId, PropSprite[]>();
  private waterTiles = new Map<MapId, { x: number; y: number }[]>();
  private flocks = new Flocks();
  scale = 2;

  constructor(private canvas: HTMLCanvasElement) {
    this.ctx = canvas.getContext('2d', { alpha: false })!;
  }

  resize(viewW: number, viewH: number, scale: number): void {
    this.scale = scale;
    setDetail(scale);
    setPropScale(scale);
    this.props.clear();
    this.canvas.width = Math.round(viewW * scale);
    this.canvas.height = Math.round(viewH * scale);
  }

  private ground(world: World): Ground {
    let g = this.grounds.get(world.map.id);
    if (!g) {
      g = new Ground(world.map);
      this.grounds.set(world.map.id, g);
      this.foliage.set(world.map.id, buildFoliage(world.map));
      const { w, h } = mapSize(world.map);
      this.clouds.set(world.map.id, new Clouds(w, h));
    }
    return g;
  }

  /** Build the ground around the player in one go (called behind the map-change fade). */
  warm(world: World): void {
    this.ground(world).warm(world.player.x, world.player.y, Math.max(world.viewW, world.viewH));
  }

  private propsFor(world: World): PropSprite[] {
    let p = this.props.get(world.map.id);
    if (!p) {
      p = buildProps(world.map);
      this.props.set(world.map.id, p);
      const water: { x: number; y: number }[] = [];
      world.map.rows.forEach((row, ty) => [...row].forEach((ch, tx) => ch === '~' && water.push({ x: tx * T, y: ty * T })));
      this.waterTiles.set(world.map.id, water);
    }
    return p;
  }

  draw(world: World, overlay: Overlay): void {
    const c = this.ctx;
    const s = S();
    const Z = this.scale;
    // Sky package: earthquake screen shake offsets the camera (0 otherwise).
    const shk = shakeOffset();
    const camX = Math.round((world.camX + shk.x) * Z) / Z, camY = Math.round((world.camY + shk.y) * Z) / Z;
    const vw = world.viewW, vh = world.viewH;
    const { w: mw, h: mh } = mapSize(world.map);
    c.setTransform(1, 0, 0, 1, 0, 0);
    c.imageSmoothingEnabled = false;

    const t = world.time;
    const dt = Math.min(0.1, Math.max(0, t - this.lastT));
    this.lastT = t;
    wind.update(dt);

    // Sky, horizon and the void beyond the map, under the ground layer.
    const camView = { x: camX, y: camY, w: vw, h: vh };
    c.setTransform(Z, 0, 0, Z, -camX * Z, -camY * Z);
    drawBackdrop(c, world.map, camView, s.time, t);
    c.setTransform(1, 0, 0, 1, 0, 0);

    // Ground layer (only the visible chunks; one more is prefetched per frame).
    const ground = this.ground(world);
    ground.draw(c, camX, camY, vw, vh, Z);
    ground.prefetch(camX, camY, vw, vh);
    c.setTransform(Z, 0, 0, Z, -camX * Z, -camY * Z);
    void mw;
    void mh;

    const view = { x0: camX - 20, x1: camX + vw + 20, y0: camY - 20, y1: camY + vh + 60 };
    const day = dayId(s);
    const night = skyTint(s.time).night;
    setSun(s.time, world.map.indoor);

    // Water: drifting wave crests and sparkles on top of the static layer.
    const ws = wind.strength();
    for (const w of this.waterTiles.get(world.map.id) ?? []) {
      if (w.x < view.x0 || w.x > view.x1 || w.y < view.y0 || w.y > view.y1) continue;
      for (let k = 0; k < 2; k++) {
        const ph = t * (0.6 + ws * 0.6) + w.y * 0.13 + k * 3.1 + w.x * 0.02;
        const wx = w.x + ((ph * 6 + k * 7 + w.y) % 16);
        const wy = w.y + 4 + k * 7 + Math.sin(ph) * 1.2;
        const a = 0.18 + 0.18 * Math.sin(ph * 1.7);
        c.fillStyle = `rgba(255,255,255,${a})`;
        c.fillRect(wx, wy, 3 + ws * 2, 0.5);
      }
      const sp = Math.sin(t * 3 + w.x * 0.37 + w.y * 0.71);
      if (sp > 0.93) {
        c.fillStyle = `rgba(255,255,255,${(sp - 0.93) * 12})`;
        c.fillRect(w.x + ((w.x * 7) % 13), w.y + ((w.y * 3) % 11), 1, 1);
      }
    }

    // Grass blades and flowers sway with the wind and part around people.
    const pushers = [world.player, ...Array.from(world.npcs.values(), (n) => n.actor)];
    const fol = this.foliage.get(world.map.id);
    drawBuildingShadows(c, world.map, camView);
    world.fx.drawDecals(c, view.x0, view.x1, view.y0, view.y1);
    if (fol) drawFoliage(c, fol, { x0: view.x0, x1: view.x1, y0: view.y0, y1: view.y1 + 20 }, pushers);

    const list: Drawable[] = [];
    for (const p of this.propsFor(world)) {
      if (p.days && !p.days.includes(day)) continue;
      if (p.visible && !p.visible()) continue;
      if (p.x1 < view.x0 || p.x0 > view.x1 || p.bottom < view.y0 || p.top > view.y1) continue;
      list.push({ y: p.sortY, draw: () => p.draw(c, t) });
    }

    for (const npc of NPCS) {
      const rt = world.npcs.get(npc.id);
      if (!rt) continue;
      const a = rt.actor;
      if (a.x < view.x0 || a.x > view.x1 || a.y < view.y0 || a.y - 40 > view.y1) continue;
      list.push({
        y: a.y,
        draw: () => {
          if (npc.look) drawCharacter(c, npc.look, a.x, a.y, a.pose());
          else if (npc.id === 'horse' || npc.id === 'pony') drawHorse(c, a.x, a.y, a.pose(), t, npc.id === 'pony');
          else drawCat(c, a.x, a.y, a.pose(), t);
          const focused = world.near?.kind === 'npc' && world.near.npc.id === npc.id;
          if (!focused && npc.look) {
            c.globalAlpha = 0.85;
            pill(c, npcName(npc.label), a.x, a.y - 34, '#ffe08a');
            c.globalAlpha = 1;
          }
          if (npc.id === 'xiaoyu' && s.xiaoyu.phase === XiaoyuPhase.Seated) bubble(c, a.x, a.y - 56, t, '!');
          else if (npc.look) {
            const mk = npcMarker(npc.id, s);
            if (mk) bubble(c, a.x, a.y - (focused ? 44 : 52), t, mk, mk === '?' ? '#8fd3ff' : '#ffd84a');
          }
        },
      });
    }

    // NPCs who meet chat with little emoji bubbles, taking turns.
    const EMOTES = ['💬', '😄', '🎵', '❓', '❗', '😂', '👍', '🌸'];
    world.chatPairs().forEach(([a, b], i) => {
      const turn = Math.floor(t / 2.2 + i) % 2;
      const who = world.npcs.get(turn ? a : b)!.actor;
      const e = EMOTES[Math.floor(t / 4.4 + i * 3) % EMOTES.length];
      list.push({ y: who.y + 0.5, draw: () => emote(c, who.x, who.y - 40, e, (t / 2.2) % 1) });
    });

    for (const d of world.ambient.drawables(world.map.id, t, night)) list.push({ y: d.y, draw: () => d.draw(c) });

    for (const mk of overlay.markers) {
      if (mk.x < view.x0 || mk.x > view.x1 || mk.y < view.y0 || mk.y > view.y1) continue;
      list.push({ y: mk.y - 1, draw: () => drawMarker(c, mk, t) });
    }

    const p = world.player;
    const baseLook = PLAYER_LOOKS[s.player.look] ?? PLAYER_LOOKS[0];
    const outfit = OUTFITS[s.player.outfit ?? 0];
    const dressed = s.player.outfit ? { ...baseLook, ...outfit } : baseLook;
    // A yellow safety helmet on the Taroko trails.
    const helmet = (world.map.id === 'hualien' && s.daily.helmet) || riding.on === 'ebike';
    const look = helmet ? { ...dressed, hairStyle: 'cap' as const, hat: riding.on === 'ebike' ? '#e2493b' : '#ffd34e' } : dressed;
    const ride = world.map.id === 'town' ? world.ride : null;
    if (ride) {
      const open = ride.phase === 'board' || ride.phase === 'alight';
      list.push({ y: ride.y + 4, draw: () => drawBus(c, ride.x, ride.y, ride.dir, night, open, t) });
    }
    if (!world.inBus) list.push({
      y: p.y,
      draw: () => {
        if (riding.on === 'bike') {
          drawBike(c, p.x, p.y, p.facing, p.phase);
          drawCharacter(c, look, p.x, p.y - 5, { ...p.pose(), moving: false });
        } else if (riding.on === 'kick') {
          drawKick(c, p.x, p.y, p.facing, p.phase, t);
          drawCharacter(c, look, p.x, p.y - 3, { ...p.pose(), moving: false });
        } else if (riding.on === 'ebike') {
          drawEbike(c, p.x, p.y, p.facing, p.phase, t);
          drawCharacter(c, look, p.x, p.y - 7, { ...p.pose(), moving: false });
        } else if (riding.on) {
          // Sit on the horse: horse below, rider raised onto the saddle.
          drawHorse(c, p.x, p.y, p.pose(), t, riding.on === 'pony', true);
          drawCharacter(c, look, p.x, p.y - (riding.on === 'pony' ? 10 : 13), { ...p.pose(), moving: false });
        } else drawCharacter(c, look, p.x, p.y, world.hopZ ? { ...p.pose(), moving: false, z: world.hopZ } : p.pose());
      },
    });
    list.push({ y: world.pet.y, draw: () => drawPet(c, s.player.pet, world.pet.x, world.pet.y, p.pose(), world.pet.hop, t) });
    if (s.bag.kitten > 0) list.push({ y: world.kitten.y, draw: () => drawCat(c, world.kitten.x, world.kitten.y, world.kitten.actor.pose(), t, true, '#d8d0c4') });

    list.sort((a, b) => a.y - b.y);
    for (const d of list) d.draw();

    world.fx.draw(c, view.y0, view.y1, t);
    if (!world.map.indoor) {
      const cl = this.clouds.get(world.map.id)!;
      cl.update(dt);
      cl.draw(c, view, 1 - night);
    }
    this.flocks.update(dt, camView, biomeOf(world.map), nightOf(s.time));
    this.flocks.draw(c, t);
    // Sky package: rain, ripples, umbrellas, typhoon debris (runs its per-frame logic too).
    drawWeather(c, world, { x: camX, y: camY, w: vw, h: vh }, Z, dt);

    // Tap target ring and a dotted preview of the walking route.
    const route = world.route();
    if (route.length) {
      c.fillStyle = 'rgba(255,216,74,0.55)';
      let prev = { x: p.x, y: p.y };
      for (const wp of route) {
        const d = Math.hypot(wp.x - prev.x, wp.y - prev.y);
        for (let k = 8; k < d; k += 8) c.fillRect(Math.round(prev.x + ((wp.x - prev.x) * k) / d) - 1, Math.round(prev.y + ((wp.y - prev.y) * k) / d) - 1, 2, 2);
        prev = wp;
      }
    }
    if (world.tapMark) {
      const k = world.tapMark.age / 0.6;
      c.globalAlpha = 1 - k;
      c.strokeStyle = '#ffd84a';
      c.lineWidth = 1.5;
      c.beginPath();
      c.ellipse(world.tapMark.x, world.tapMark.y, 4 + k * 8, 2 + k * 4, 0, 0, Math.PI * 2);
      c.stroke();
      c.globalAlpha = 1;
    }

    drawLighting(c, { x: camX, y: camY, w: vw, h: vh }, s.time, world.map, [...world.ambient.lights(world.map.id), ...(world.ride && world.map.id === 'town' ? [{ x: world.ride.x + world.ride.dir * 46, y: world.ride.y - 4, r: 40, color: '255,240,190' }] : [])]);

    if (world.near) {
      const r = targetRect(world.near, s, world);
      const near = world.near;
      const label = near.kind === 'npc' ? npcName(near.npc.label) : near.kind === 'spot' ? m().treasure.dig : objName(near.obj.label);
      const icon = near.kind === 'npc' ? '💬' : near.kind === 'spot' ? '' : near.obj.icon;
      const by = (world.near.kind === 'npc' ? r.y - 30 : r.y - 38) + Math.sin(t * 4) * 1.5;
      pill(c, `${icon} ${label}`, r.x + r.w / 2, by, '#fff3d0', 'rgba(150,90,20,0.92)');
    }

    if (overlay.guide) drawGuide(c, overlay.guide, { x: camX, y: camY, w: vw, h: vh }, p, t);

    world.fx.drawFloaters(c);

    if (world.fade > 0) {
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.fillStyle = `rgba(10,6,4,${world.fade})`;
      c.fillRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }
}

function emote(c: Ctx, x: number, y: number, e: string, k: number): void {
  const pop = k < 0.12 ? k / 0.12 : k > 0.88 ? (1 - k) / 0.12 : 1;
  if (pop <= 0) return;
  c.save();
  c.translate(x, y);
  c.scale(pop, pop);
  c.fillStyle = 'rgba(255,255,255,0.95)';
  c.strokeStyle = 'rgba(42,24,16,0.6)';
  c.lineWidth = 0.6;
  c.beginPath();
  c.roundRect(-6, -6, 12, 10, 4);
  c.fill();
  c.stroke();
  c.beginPath();
  c.moveTo(-1.5, 4);
  c.lineTo(0, 6.5);
  c.lineTo(1.5, 4);
  c.fill();
  c.font = '7px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillText(e, 0, -0.5);
  c.restore();
}

function bubble(c: Ctx, x: number, y: number, t: number, ch: string, color = '#ffd84a'): void {
  const b = Math.sin(t * 5) * 1.5;
  ellipse(c, '#2a1810', x, y + b, 6, 6);
  ellipse(c, color, x, y + b, 5, 5);
  c.fillStyle = '#2a1810';
  c.font = '800 8px sans-serif';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillText(ch, x, y + b + 0.5);
}

function drawMarker(c: Ctx, mk: Marker, t: number): void {
  const { x, y } = mk;
  if (mk.kind === 'clover') {
    const b = Math.sin(t * 3 + x) * 1.5;
    shadow(c, x, y, 4, 1.5);
    for (const [dx, dy] of [[-2, -2], [2, -2], [-2, 2], [2, 2]]) ellipse(c, '#2a1810', x + dx, y - 7 + dy + b, 2.6, 2.6);
    for (const [dx, dy] of [[-2, -2], [2, -2], [-2, 2], [2, 2]]) ellipse(c, '#3fd36a', x + dx, y - 7 + dy + b, 2, 2);
    rect(c, '#2f8a3a', x, y - 5 + b, 1, 4);
    if (Math.sin(t * 4 + y) > 0.7) rect(c, '#fffbe0', x + 3, y - 12 + b, 1, 1);
    return;
  }
  if (mk.kind === 'checkpoint') {
    shadow(c, x, y, 5, 2);
    rect(c, '#2a1810', x - 1, y - 22, 2, 22);
    const wave = Math.sin(t * 6) * 1.5;
    c.fillStyle = '#ffd84a';
    c.beginPath();
    c.moveTo(x + 1, y - 22);
    c.lineTo(x + 12, y - 18 + wave);
    c.lineTo(x + 1, y - 14);
    c.fill();
    if (mk.label) pill(c, mk.label, x, y - 26, '#2a1810', 'rgba(255,216,74,0.95)');
    return;
  }
  if (mk.kind === 'glint') {
    // Something shiny buried here: a twinkle and a disturbed patch of earth.
    c.fillStyle = 'rgba(90,60,30,0.35)';
    c.beginPath();
    c.ellipse(x, y, 6, 2.5, 0, 0, Math.PI * 2);
    c.fill();
    const k = (t * 1.3 + x * 0.01) % 1;
    if (k < 0.5) {
      const a = Math.sin(k * Math.PI * 2);
      c.fillStyle = `rgba(255,250,200,${a})`;
      c.fillRect(x - 0.5, y - 5 - a * 3, 1, 5 + a * 4);
      c.fillRect(x - 2.5 - a, y - 3, 5 + a * 2, 1);
    }
    return;
  }
  // A rustling bush: leaves popping out every now and then.
  const k = (t * 1.5) % 1;
  if (k < 0.3) {
    rect(c, '#69c06a', x - 6 + k * 10, y - 18 - k * 20, 2, 2);
    rect(c, '#3f9a4a', x + 4 - k * 6, y - 16 - k * 16, 2, 2);
  }
}

/** Bouncing marker over an on-screen target, or an edge arrow pointing to an off-screen one. */
function drawGuide(c: Ctx, g: { x: number; y: number }, v: { x: number; y: number; w: number; h: number }, p: { x: number; y: number }, t: number): void {
  const margin = 26;
  const inView = g.x > v.x + margin && g.x < v.x + v.w - margin && g.y - 30 > v.y + 90 && g.y < v.y + v.h - 110;
  if (Math.hypot(g.x - p.x, g.y - p.y) < 28) return;
  if (inView) {
    const b = Math.abs(Math.sin(t * 4)) * 4;
    const y = g.y - 46 - b;
    c.fillStyle = '#2a1810';
    c.beginPath();
    c.moveTo(g.x - 6, y - 1);
    c.lineTo(g.x + 6, y - 1);
    c.lineTo(g.x, y + 8);
    c.fill();
    c.fillStyle = '#ffd84a';
    c.beginPath();
    c.moveTo(g.x - 4.5, y);
    c.lineTo(g.x + 4.5, y);
    c.lineTo(g.x, y + 6.5);
    c.fill();
    return;
  }
  const cx = v.x + v.w / 2, cy = v.y + v.h / 2;
  const ang = Math.atan2(g.y - cy, g.x - cx);
  const hw = v.w / 2 - margin, top = 100, bottom = 120;
  const hh = ang < 0 ? v.h / 2 - top : v.h / 2 - bottom;
  const k = Math.min(Math.abs(hw / Math.cos(ang)), Math.abs(hh / Math.sin(ang)));
  const ax = cx + Math.cos(ang) * k, ay = cy + Math.sin(ang) * k;
  const pulse = 1 + Math.sin(t * 5) * 0.08;
  c.save();
  c.translate(ax, ay);
  c.rotate(ang);
  c.scale(pulse, pulse);
  c.fillStyle = 'rgba(42,24,16,0.9)';
  c.beginPath();
  c.arc(0, 0, 11, 0, Math.PI * 2);
  c.fill();
  c.fillStyle = '#ffd84a';
  c.beginPath();
  c.moveTo(8, 0);
  c.lineTo(-4, -6);
  c.lineTo(-1, 0);
  c.lineTo(-4, 6);
  c.closePath();
  c.fill();
  c.restore();
}
